import React, { useState, useEffect } from 'react';
import "../covfile/Install.css";
import { useAuth } from '../auth/Auth'; 

function FrontInstallationReports() {
  const { user } = useAuth(); 
  const [installationReports, setInstallationReports] = useState([]);
  const [groupedReports, setGroupedReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archivedSessions, setArchivedSessions] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [isGeneralPoolCollapsed, setIsGeneralPoolCollapsed] = useState(false);
  const [isArchivePoolCollapsed, setIsArchivePoolCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userToken, setUserToken] = useState(null);
  const [theme, setTheme] = useState('white'); // Додано стан для теми
  
  const userId = user?.user_id || localStorage.getItem('userId') || '';

  // Функція для завантаження конфігурації теми
  const fetchThemeConfig = async () => {
    try {
      const response = await fetch(`https://hub2.daolog.net/user-theme-config/${userId}`);
      if (response.ok) {
        const config = await response.json();
        setTheme(config.theme || 'white');
        console.log('Theme loaded:', config.theme);
      } else {
        console.error('Failed to fetch theme config:', response.status);
        setTheme('white');
      }
    } catch (error) {
      console.error('Error fetching theme config:', error);
      setTheme('white');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Завантажуємо тему при завантаженні компонента
  useEffect(() => {
    fetchThemeConfig();
  }, [userId]);

  const handlePipToggle = () => {
    if (window.__interactiveTabPiP && window.__interactiveTabPiP.toggle) {
      window.__interactiveTabPiP.toggle({
        mode: 'element-only',
        targetSelector: '.pip-target-element' 
      });
    } else {
      alert('Для использования этой функции необходимо установить расширение "Interactive Tab PiP".');
    }
  };

  const refreshData = () => {
    setRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchUserToken = async () => {
      if (!userId) {
        console.log('No userId available, skipping token fetch');
        setLoading(false);
        return;
      }

      try {
        const tokenResponse = await fetch(`https://hub2.daolog.net/api/user-token/${userId}`);
        
        if (!tokenResponse.ok) {
          throw new Error(`Помилка отримання токену: ${tokenResponse.status} ${tokenResponse.statusText}`);
        }
        
        const tokenData = await tokenResponse.json();
        const fetchedToken = tokenData.token;
        
        if (!fetchedToken) {
          throw new Error('Токен користувача не знайдено');
        }
        
        setUserToken(fetchedToken);
        return fetchedToken;
      } catch (error) {
        console.error('Помилка завантаження токену:', error);
        setError(`Помилка завантаження токену: ${error.message}`);
        return null;
      }
    };
    
    fetchUserToken();
  }, [userId, refreshTrigger]);

  // Функція для завантаження архівованих сесій
  const fetchArchivedSessions = async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`https://hub2.daolog.net/api/archived-sessions-front/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setArchivedSessions(new Set(data.archivedSessions || []));
        }
      } else {
        // Якщо файл не знайдено, створюємо пустий архів
        setArchivedSessions(new Set());
      }
    } catch (error) {
      console.error('Помилка завантаження архівованих сесій:', error);
      // Якщо помилка, все одно створюємо пустий Set
      setArchivedSessions(new Set());
    }
  };

  useEffect(() => {
    fetchArchivedSessions();
  }, [userId, refreshTrigger]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId || !userToken) {
        console.log('No userId or userToken available, skipping data fetch');
        setLoading(false);
        return;
      }
  
      try {
        setError('');
        
        const response = await fetch(`https://hub2.daolog.net/api/installation-reports-front/latest?limit=100`);
        
        if (!response.ok) {
          throw new Error(`Помилка отримання звітів: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        const reportsArray = data.reports || [];
        
        if (Array.isArray(reportsArray)) {
          const filteredByToken = reportsArray.filter(report => report.token === userToken);
          const filteredReports = filteredByToken.filter(report => report.session_id);
          
          const sortedData = [...filteredReports].sort((a, b) => {
            return new Date(b.timestamp) - new Date(a.timestamp);
          });
          
          setInstallationReports(sortedData);
          
          const grouped = sortedData.reduce((acc, report) => {
            const { session_id } = report;
            if (!session_id) return acc;
            
            if (!acc[session_id]) {
              acc[session_id] = [];
            }
            acc[session_id].push(report);
            return acc;
          }, {});
          
          setGroupedReports(grouped);
          
          if (sortedData.length === 0 && userToken) {
            setError('Не знайдено звітів');
          }
        } else {
          setError('Невірний формат даних від сервера');
        }
      } catch (error) {
        console.error('Помилка завантаження даних:', error);
        setError(`Помилка завантаження даних: ${error.message}`);
      } finally {
        setRefreshing(false);
        setLoading(false);
      }
    };
    
    if (userToken) {
      fetchData();
    }
  }, [userId, userToken, refreshTrigger]);

  const archiveSession = async (sessionId, event) => {
    event?.stopPropagation();
    
    try {
      const response = await fetch(`https://hub2.daolog.net/api/archive-session-front/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Оновлюємо локальний стан після успішного архівування
          const newArchivedSessions = new Set(archivedSessions);
          newArchivedSessions.add(sessionId);
          setArchivedSessions(newArchivedSessions);
          
          // Також можна оновити дані для переконання
          setTimeout(() => {
            fetchArchivedSessions();
          }, 500);
        }
      } else {
        console.error('Помилка архівування: сервер повернув помилку');
      }
    } catch (error) {
      console.error('Помилка архівування сесії:', error);
      alert('Помилка при архівуванні сесії. Спробуйте ще раз.');
    }
  };

  const unarchiveSession = async (sessionId, event) => {
    event?.stopPropagation();
    
    try {
      const response = await fetch(`https://hub2.daolog.net/api/unarchive-session-front/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Оновлюємо локальний стан після успішного видалення з архіву
          const newArchivedSessions = new Set(archivedSessions);
          newArchivedSessions.delete(sessionId);
          setArchivedSessions(newArchivedSessions);
          
          // Також можна оновити дані для переконання
          setTimeout(() => {
            fetchArchivedSessions();
          }, 500);
        }
      } else {
        console.error('Помилка відновлення: сервер повернув помилку');
      }
    } catch (error) {
      console.error('Помилка відновлення сесії:', error);
      alert('Помилка при відновленні сесії. Спробуйте ще раз.');
    }
  };

  const getLatestReport = (sessionReports) => {
    if (!sessionReports || sessionReports.length === 0) return null;
    return sessionReports[0];
  };

  const getServerAddressForSession = (sessionReports) => {
    if (!sessionReports || sessionReports.length === 0) return 'SyrvePOS2';
    
    for (const report of sessionReports) {
      if (report.server_address && 
          report.server_address !== 'None' && 
          report.server_address !== 'null' && 
          report.server_address.trim() !== '') {
        return report.server_address;
      }
    }
    
    return 'SyrvePOS2';
  };

  const getInstallationStatus = (reports) => {
    if (!reports || reports.length === 0) return 'no_data';
    
    const latestReport = reports[0];
    
    // Перевіряємо, чи є дія restart_pc
    if (latestReport.action === 'restart_pc') {
      // Якщо error не пустий (не "-" і не порожній), то це помилка
      if (latestReport.error && 
          latestReport.error.trim() !== '' && 
          latestReport.error.trim() !== '-') {
        return 'error';
      }
      
      // Якщо error пустий, перевіряємо чи були помилки в процесі
      const hasErrors = reports.some(report => 
        !report.success && report.error && 
        report.error.trim() !== '' && 
        report.error.trim() !== '-'
      );
      
      if (hasErrors) {
        return 'completed_with_errors';
      } else {
        return 'completed';
      }
    }
    
    // Перевіряємо, чи є дія error (як окрема дія)
    if (latestReport.action === 'error') {
      return 'error';
    }
    
    // Перевіряємо, чи є дія start
    if (latestReport.action === 'start') {
      return 'in_progress';
    }
    
    // Якщо немає start, але є інші дії після start
    const hasStartAction = reports.some(report => report.action === 'start');
    if (hasStartAction && latestReport.action !== 'restart_pc') {
      return 'in_progress';
    }
    
    return 'unknown';
  };

  const getDisplayStatus = (reports) => {
    const status = getInstallationStatus(reports);
    
    switch (status) {
      case 'completed':
        return 'Успішно';
      case 'completed_with_errors':
        return 'Встановлено (є помилки)';
      case 'in_progress':
        return 'Встановлення...';
      case 'error':
        return 'Помилка';
      case 'no_data':
        return 'Немає даних';
      default:
        return 'Очікування';
    }
  };

  const getInstallationStartTime = (reports) => {
    if (!reports || reports.length === 0) return null;
    
    for (let i = reports.length - 1; i >= 0; i--) {
      const report = reports[i];
      if (report.action === 'start') {
        return report.timestamp;
      }
    }
    
    return null;
  };

  const getInstallationEndTime = (reports) => {
    if (!reports || reports.length === 0) return null;
    
    for (let i = reports.length - 1; i >= 0; i--) {
      const report = reports[i];
      if (report.action === 'restart_pc') {
        return report.timestamp;
      }
    }
    
    return null;
  };

  const Timer = ({ startTime, endTime, status }) => {
    const [currentTime, setCurrentTime] = useState(Date.now());
    
    useEffect(() => {
      let intervalId;
      
      if (status === 'in_progress' && startTime && !endTime) {
        intervalId = setInterval(() => {
          setCurrentTime(Date.now());
        }, 1000);
      }
      
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [status, startTime, endTime]);
    
    if (status !== 'in_progress' || !startTime || endTime) return null;
    
    const startTimestamp = new Date(startTime).getTime();
    const timeDiff = currentTime - startTimestamp;
    const seconds = Math.floor(timeDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    return (
      <span className="server-timer">
        ({hours > 0 ? `${hours}г ` : ''}{minutes % 60}хв {seconds % 60}с)
      </span>
    );
  };

  const getTotalDuration = (reports) => {
    const startTime = getInstallationStartTime(reports);
    const endTime = getInstallationEndTime(reports);
    
    if (!startTime) return null;
    
    const endTimestamp = endTime ? new Date(endTime).getTime() : Date.now();
    const startTimestamp = new Date(startTime).getTime();
    const timeDiff = endTimestamp - startTimestamp;
    
    const seconds = Math.floor(timeDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    return { hours, minutes: minutes % 60, seconds: seconds % 60 };
  };

  const getAllSessions = () => {
    return Object.keys(groupedReports)
      .filter(sessionId => showArchived ? archivedSessions.has(sessionId) : !archivedSessions.has(sessionId))
      .map(sessionId => {
        const reports = groupedReports[sessionId];
        const latestReport = getLatestReport(reports);
        const serverAddress = getServerAddressForSession(reports);
        const status = getInstallationStatus(reports);
        const startTime = getInstallationStartTime(reports);
        const endTime = getInstallationEndTime(reports);
        const duration = getTotalDuration(reports);
        
        return {
          sessionId,
          serverAddress,
          reports,
          latestReport,
          displayStatus: getDisplayStatus(reports),
          status,
          isSuccess: status === 'completed',
          isCompletedWithErrors: status === 'completed_with_errors',
          isInProgress: status === 'in_progress',
          isError: status === 'error',
          startTime,
          endTime,
          duration,
          latestTimestamp: latestReport ? new Date(latestReport.timestamp) : new Date(0),
          isArchived: archivedSessions.has(sessionId),
          hasErrors: reports.some(r => 
            !r.success && r.error && 
            r.error.trim() !== '' && 
            r.error.trim() !== '-'
          )
        };
      }).sort((a, b) => {
        return b.latestTimestamp - a.latestTimestamp;
      });
  };

  const [timerUpdate, setTimerUpdate] = useState(0);
  
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimerUpdate(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const createPipWindow = (serverAddress, sessionId, reports) => {
    const pipWindow = window.open('', 'pipWindow', 
      'width=1000,height=850,popup=yes,alwaysRaised=yes,alwaysOnTop=yes');
    
    if (!pipWindow) {
      alert('Спливаючі вікна заблоковані браузером. Будь ласка, дозвольте їх для цього сайту.');
      return;
    }
    
    const isDarkTheme = theme === 'dark';
    
    const style = pipWindow.document.createElement('style');
    style.textContent = `
      body {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        margin: 0;
        padding: 0;
        overflow-x: hidden;
        background-color: white;
      }
      
      .pip-container {
        width: 100%;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
      
      .pip-container.dark-theme {
        background-color: #1a202c;
        color: #e2e8f0;
      }
      
      .pip-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px;
        background-color: #282c34;
        color: white;
        position: sticky;
        top: 0;
        z-index: 100;
      }
      
      .pip-container.dark-theme .pip-header {
        background-color: #1a202c;
      }
      
      .pip-header h3 {
        margin: 0;
        font-size: 16px;
      }
      
      .header-buttons {
        display: flex;
        gap: 8px;
      }
      
      .pip-button {
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
        font-size: 12px;
        transition: background-color 0.3s;
      }
      
      .exit-pip-button {
        background-color: #f44336;
        color: white;
      }
      
      .pip-container.dark-theme .exit-pip-button {
        background-color: #f56565;
      }
      
      .exit-pip-button:hover {
        background-color: #d32f2f;
      }
      
      .pip-container.dark-theme .exit-pip-button:hover {
        background-color: #e53e3e;
      }
      
      .download-button {
        background-color: #4CAF50;
        color: white;
      }
      
      .pip-container.dark-theme .download-button {
        background-color: #48bb78;
      }
      
      .download-button:hover {
        background-color: #388E3C;
      }
      
      .pip-container.dark-theme .download-button:hover {
        background-color: #38a169;
      }
      
      .pip-content {
        flex: 1;
        padding: 10px;
        overflow-y: auto;
      }
      
      .session-info {
        margin-bottom: 15px;
        padding: 15px;
        background-color: #f8f9fa;
        border-radius: 5px;
        border-left: 4px solid #007bff;
      }
      
      .pip-container.dark-theme .session-info {
        background-color: #2d3748;
        border-left-color: #3182ce;
      }
      
      .session-info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 10px;
      }
      
      .info-item {
        margin: 5px 0;
      }
      
      .info-label {
        font-weight: bold;
        color: #495057;
      }
      
      .pip-container.dark-theme .info-label {
        color: #a0aec0;
      }
      
      .info-value {
        color: #212529;
        word-break: break-all;
      }
      
      .pip-container.dark-theme .info-value {
        color: #e2e8f0;
      }
      
      .duration-info {
        background-color: #e8f5e9;
        padding: 8px;
        border-radius: 4px;
        margin: 10px 0;
      }
      
      .pip-container.dark-theme .duration-info {
        background-color: #2d3c50;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 15px;
        background-color: white;
      }
      
      .pip-container.dark-theme table {
        background-color: #2d3748;
      }
      
      th, td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
        font-size: 12px;
      }
      
      .pip-container.dark-theme th,
      .pip-container.dark-theme td {
        border-color: #4a5568;
      }
      
      th {
        background-color: #f2f2f2;
        font-weight: bold;
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      .pip-container.dark-theme th {
        background-color: #414752;
        color: #ffffff;
      }
      
      tr:nth-child(even) {
        background-color: #f5f5f5;
      }
      
      .pip-container.dark-theme tr:nth-child(even) {
        background-color: #2d3c50;
      }
      
      tr:hover {
        background-color: #e9e9e9;
      }
      
      .pip-container.dark-theme tr:hover {
        background-color: #4a5568;
      }
      
      .success-true {
        color: green;
        font-weight: bold;
      }
      
      .pip-container.dark-theme .success-true {
        color: #68d391;
      }
      
      .success-false {
        color: red;
        font-weight: bold;
      }
      
      .pip-container.dark-theme .success-false {
        color: #fc8181;
      }
      
      .error-row {
        background-color: #ffebee !important;
      }
      
      .pip-container.dark-theme .error-row {
        background-color: #3a2d2d !important;
      }
      
      .error-message {
        max-width: 300px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        hyphens: auto;
        color: #d32f2f;
        font-size: 11px;
      }
      
      .pip-container.dark-theme .error-message {
        color: #fc8181;
      }
      
      .action-cell {
        font-weight: bold;
      }
      
      .timestamp-cell {
        font-family: monospace;
        font-size: 11px;
      }
      
      td {
        background-color: white;
        color: #333;
      }
      
      .pip-container.dark-theme td {
        background-color: #2d3748;
        color: #e2e8f0;
      }
      
      .pip-container.dark-theme tr:nth-child(even) td {
        background-color: #2d3c50;
      }
    `;
    pipWindow.document.head.appendChild(style);
    
    const sessionData = reports || [];
    const latestReport = sessionData[0] || {};
    const status = getInstallationStatus(sessionData);
    const startTime = getInstallationStartTime(sessionData);
    const endTime = getInstallationEndTime(sessionData);
    const duration = getTotalDuration(sessionData);
    const hasErrors = sessionData.some(r => 
      !r.success && r.error && 
      r.error.trim() !== '' && 
      r.error.trim() !== '-'
    );

    const formatDateTime = (dateTimeString) => {
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('uk-UA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return dateTimeString;
      }
    };
    
    const formatDuration = (dur) => {
      if (!dur) return '';
      return `${dur.hours > 0 ? `${dur.hours}г ` : ''}${dur.minutes}хв ${dur.seconds}с`;
    };
    
    const content = `
      <div class="pip-container ${isDarkTheme ? 'dark-theme' : ''}">
        <div class="pip-header">
          <h3>Звіти встановлення Front - ${serverAddress}</h3>
          <div class="header-buttons">
            <button class="pip-button download-button" id="download-button">
              Завантажити файл сесії
            </button>
            <button class="pip-button exit-pip-button" id="exit-button">
              Закрити
            </button>
          </div>
        </div>
        
        <div class="pip-content">
          <div class="session-info">
            <div class="session-info-grid">
              <div class="info-item">
                <div class="info-label">Комп'ютер:</div>
                <div class="info-value">${serverAddress}</div>
              </div>
              <div class="info-item">
                <div class="info-label">ID Сесії:</div>
                <div class="info-value" style="font-family: monospace; font-size: 12px;">${sessionId}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Статус:</div>
                <div class="info-value">
                  ${status === 'completed' ? 'Успішно' : 
                    status === 'completed_with_errors' ? 'Встановлено (є помилки)' :
                    status === 'in_progress' ? 'Встановлення...' :
                    status === 'error' ? 'Помилка' : 'Очікування'}
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Початок:</div>
                <div class="info-value">${startTime ? formatDateTime(startTime) : 'Невідомо'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Завершення:</div>
                <div class="info-value">${endTime ? formatDateTime(endTime) : 'Не завершено'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Записів:</div>
                <div class="info-value">${sessionData.length}</div>
              </div>
            </div>
            
            ${duration ? `
              <div class="duration-info">
                <strong>Тривалість встановлення:</strong> ${formatDuration(duration)}
              </div>
            ` : ''}
            
            ${hasErrors ? `
              <div style="margin-top: 10px; padding: 8px; background-color: #fff3cd; border-radius: 4px; color: #856404;">
                В процесі встановлення були помилки
              </div>
            ` : ''}
          </div>
          
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Дія</th>
                  <th>Тип</th>
                  <th>Версія</th>
                  <th>Статус</th>
                  <th>Час</th>
                  <th>Помилка</th>
                </tr>
              </thead>
              <tbody id="table-body">
                ${sessionData.length > 0 ? 
                  sessionData.map((report, index) => `
                    <tr class="${!report.success && report.error && report.error.trim() !== '-' ? 'error-row' : ''}">
                      <td class="action-cell">${report.action || '-'}</td>
                      <td>${report.install_type || '-'}</td>
                      <td>${report.version || '-'}</td>
                      <td class="success-${report.success}">
                        ${report.success ? 'Успішно' : 'Помилка'}
                      </td>
                      <td class="timestamp-cell">${formatDateTime(report.timestamp)}</td>
                      <td><div class="error-message">${report.error || '-'}</div></td>
                    </tr>
                  `).join('') : '<tr><td colspan="6">Немає даних</td></tr>'}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
    
    pipWindow.document.body.innerHTML = content;
    
    // Додаємо обробники подій для кнопок
    const exitButton = pipWindow.document.getElementById('exit-button');
    const downloadButton = pipWindow.document.getElementById('download-button');
    
    if (exitButton) {
      exitButton.addEventListener('click', () => {
        pipWindow.close();
      });
    }
    
    if (downloadButton) {
      downloadButton.addEventListener('click', () => {
        // Формуємо URL для завантаження файлу сесії
        const downloadUrl = `https://hub2.daolog.net/api/webhook/download/${sessionId}`;
        
        // Створюємо тимчасовий посилання для завантаження
        const downloadLink = pipWindow.document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `${sessionId}.json`;
        downloadLink.target = '_blank';
        downloadLink.click();
      });
    }
    
    pipWindow.focus();
  };

  const togglePipMode = (serverAddress, sessionId, reports) => {
    createPipWindow(serverAddress, sessionId, reports);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshData();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  const ServerCard = ({ session }) => {
    const { 
      sessionId, 
      serverAddress, 
      reports, 
      displayStatus, 
      isSuccess, 
      isCompletedWithErrors,
      isInProgress, 
      isError, 
      startTime, 
      endTime,
      duration,
      isArchived 
    } = session;
    
    let cardVariant = 'default';
    if (isSuccess) cardVariant = 'completed';
    else if (isCompletedWithErrors) cardVariant = 'completed-with-errors'; // Додано цей варіант
    else if (isInProgress) cardVariant = 'in-progress';
    else if (isError) cardVariant = 'error';
    
    const formatDuration = (dur) => {
      if (!dur) return '';
      return `${dur.hours > 0 ? `${dur.hours}г ` : ''}${dur.minutes}хв ${dur.seconds}с`;
    };
    
    return (
      <div className={`server-card ${cardVariant} ${isArchived ? 'archived' : ''}`}>
        <div className="server-content">
          <div className="server-info">
            <div className="server-address">
              {serverAddress}
            </div>
            <div className="server-status-row">
              <span className={`server-status ${
                isSuccess ? 'success' : 
                isCompletedWithErrors ? 'completed-with-errors' : // Додано цей клас
                isInProgress ? 'progress' : 
                isError ? 'error' : 'default'
              }`}>
                {displayStatus}
              </span>
              {isInProgress && startTime && !endTime && (
                <Timer 
                  key={`${sessionId}-${timerUpdate}`}
                  startTime={startTime} 
                  endTime={endTime}
                  status={isInProgress ? 'in_progress' : 'completed'} 
                />
              )}
              {duration && endTime && (
                <span className="duration-display">
                  ({formatDuration(duration)})
                </span>
              )}
            </div>
          </div>
          
          <div className="server-actions">
            <button 
              onClick={() => togglePipMode(serverAddress, sessionId, reports)} 
              className="detail-button"
              title="Детальний перегляд звіту"
            >
              →
            </button>
            
            {!showArchived && !isMobile && (
              <button 
                onClick={(e) => archiveSession(sessionId, e)}
                className="archive-button"
                title="Відправити в архів"
              >
                Архів
              </button>
            )}
            {showArchived && !isMobile && (
              <button 
                onClick={(e) => unarchiveSession(sessionId, e)}
                className="unarchive-button"
                title="Відновити з архіву"
              >
                Відновити
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
        <div className="main-container">
          <div className="single-column" style={{marginTop: "120px"}}>
            <div className="column-header">
              <h2>Встановлення SyrveFront</h2>
            </div>
            <div className="loading">
              {userId ? 'Завантаження даних...' : 'Очікування авторизації...'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeSessions = getAllSessions().filter(s => !s.isArchived);
  const archivedSessionsList = getAllSessions().filter(s => s.isArchived);

  return (
    <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <div className="app-container">
        <div className="main-container">
          <div className="single-column">
            <div className="column-header">
              <div className="column-title">
                <h2>Встановлення SyrveFront</h2>
              </div>
              <div className="column-actions">
                <div className="view-toggle">
                  <button 
                    className={!showArchived ? 'active green-button' : 'button'}
                    onClick={() => setShowArchived(false)}
                  >
                    Поточні ({activeSessions.length})
                  </button>
                  <button 
                    className={showArchived ? 'active' : 'button'}
                    onClick={() => setShowArchived(true)}
                  >
                    Архів ({archivedSessionsList.length})
                  </button>
                </div>
              </div>
            </div>
            
            {error && (
              <div className="error-message alert">
                {error}
              </div>
            )}
            
            {!userToken && !loading && (
              <div className="no-token-message alert warning">
                Токен користувача не знайдено. Будь ласка, переконайтеся, що ви авторизовані.
              </div>
            )}
            
            {!showArchived ? (
              <div className="pool-section pip-target-element">
                <div className="pool-header">
                  <div className="pool-header-left">
                    <h2 className="pool-title">
                      Поточні встановлення
                      <span className="server-count">({activeSessions.length})</span>
                    </h2>
                  </div>
                  <div className="pool-header-right">
                    <button 
                      className="collapse-btn"
                      onClick={() => setIsGeneralPoolCollapsed(!isGeneralPoolCollapsed)}
                      aria-label={isGeneralPoolCollapsed ? "Розгорнути" : "Згорнути"}
                    >
                      {isGeneralPoolCollapsed ? '▼' : '▲'}
                    </button>
                  </div>
                </div>
                
                {!isGeneralPoolCollapsed && (
                  <div className="servers-list">
                    {activeSessions.length > 0 ? (
                      activeSessions.map((session) => (
                        <ServerCard key={session.sessionId} session={session} />
                      ))
                    ) : (
                      <div className="no-data">
                        Немає активних встановлень SyrveFront
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="pool-section pip-target-element">
                <div className="pool-header">
                  <div className="pool-header-left">
                    <h2 className="pool-title">
                      Архів встановлень
                      <span className="server-count">({archivedSessionsList.length})</span>
                    </h2>
                  </div>
                  <div className="pool-header-right">
                    <button 
                      className="collapse-btn"
                      onClick={() => setIsArchivePoolCollapsed(!isArchivePoolCollapsed)}
                      aria-label={isArchivePoolCollapsed ? "Розгорнути" : "Згорнути"}
                    >
                      {isArchivePoolCollapsed ? '▼' : '▲'}
                    </button>
                  </div>
                </div>
                
                {!isArchivePoolCollapsed && (
                  <div className="servers-list">
                    {archivedSessionsList.length > 0 ? (
                      archivedSessionsList.map((session) => (
                        <ServerCard key={session.sessionId} session={session} />
                      ))
                    ) : (
                      <div className="no-data">Немає архівованих встановлень</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="pip-controls">
              <button 
                className="pip-toggle-btn"
                title="Відкрити в режимі 'Картинка в картинці'"
                onClick={handlePipToggle}
              >
                📌 PiP Mode
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FrontInstallationReports;